"use client";

type StoredFileData = {
  name: string;
  type: string;
  content: string;
};

const isBrowser = typeof window !== "undefined";

const fileToDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const dataURLToFile = async (
  dataURL: string,
  filename: string,
  mimeType: string,
): Promise<File> => {
  const response = await fetch(dataURL);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
};

export const saveFileToStorage = async (
  key: string,
  file: File,
): Promise<void> => {
  if (!isBrowser) {
    throw new Error("File storage is only available in browser");
  }

  const content = await fileToDataURL(file);
  const data: StoredFileData = {
    name: file.name,
    type: file.type,
    content,
  };

  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    sessionStorage.setItem(key, JSON.stringify(data));
  }
};

export const getFileFromStorage = async (key: string): Promise<File> => {
  if (!isBrowser) {
    throw new Error("File storage is only available in browser");
  }

  const jsonData = localStorage.getItem(key) || sessionStorage.getItem(key);

  if (!jsonData) {
    throw new Error(`File with key "${key}" not found`);
  }

  const { name, type, content } = JSON.parse(jsonData) as StoredFileData;
  return dataURLToFile(content, name, type);
};

export const deleteFileFromStorage = (key: string): void => {
  if (!isBrowser) {
    throw new Error("File storage is only available in browser");
  }

  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};
